const hexToRgb = (hex: string) => {
  const rgbChar = ['r', 'g', 'b'] as const;

  const normal = hex.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  type Return = Record<(typeof rgbChar)[number], number>;
  if (normal) {
    return normal.slice(1).reduce<Return>((a, e, i) => {
      a[rgbChar[i]] = parseInt(e, 16);
      return a;
    }, {} as Return);
  }

  const shorthand = hex.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i);
  if (shorthand) {
    return shorthand.slice(1).reduce<Return>((a, e, i) => {
      a[rgbChar[i]] = 0x11 * parseInt(e, 16);
      return a;
    }, {} as Return);
  }

  return null;
};

const hexToRgbAString = (hex: string, a: number) => {
  const rgb = hexToRgb(hex);

  if (!rgb) return hex;
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${a})`;
};

export const WhiteColors = {
  white: '#FFFFFF',
  whiteOpacity04: 'rgba(255, 255, 255, 0.04)',
  whiteOpacity07: 'rgba(255, 255, 255, 0.07)',
  whiteOpacity12: 'rgba(255, 255, 255, 0.12)',
  whiteOpacity22: 'rgba(255, 255, 255, 0.22)',
  whiteOpacity36: 'rgba(255, 255, 255, 0.36)',
  whiteOpacity45: 'rgba(255, 255, 255, 0.45)',
  whiteOpacity52: 'rgba(255, 255, 255, 0.52)',
  whiteOpacity67: 'rgba(255, 255, 255, 0.67)',
  whiteOpacity79: 'rgba(255, 255, 255, 0.79)',
  whiteOpacity92: 'rgba(255, 255, 255, 0.92)',
} as const;

export const BlackColors = {
  black: '#000000',
  blackOpacity04: 'rgba(0, 0, 0, 0.04)',
  blackOpacity07: 'rgba(0, 0, 0, 0.07)',
  blackOpacity12: 'rgba(0, 0, 0, 0.12)',
  blackOpacity22: 'rgba(0, 0, 0, 0.22)',
  blackOpacity36: 'rgba(0, 0, 0, 0.36)',
  blackOpacity45: 'rgba(0, 0, 0, 0.45)',
  blackOpacity52: 'rgba(0, 0, 0, 0.52)',
  blackOpacity67: 'rgba(0, 0, 0, 0.67)',
  blackOpacity79: 'rgba(0, 0, 0, 0.79)',
  blackOpacity88: 'rgba(0, 0, 0, 0.88)',
  blackOpacity92: 'rgba(0, 0, 0, 0.92)',
} as const;

export const GrayColors = {
  gray30: '#F9F9F9',
  gray50: '#F6F6F6',
  gray100: '#F1F1F1',
  gray200: '#E4E4E5',
  gray300: '#C4C4C6',
  gray400: '#AEAFB1',
  gray500: '#898C8E',
  gray600: '#686B6D',
  gray700: '#4C5053',
  gray800: '#2F3436',
  gray900: '#1D2225',
  grayOpacity02: `${hexToRgbAString('#1D2225', 0.02)}`,
  grayOpacity03: `${hexToRgbAString('#1D2225', 0.03)}`,
  grayOpacity04: `${hexToRgbAString('#1D2225', 0.04)}`,
  grayOpacity05: `${hexToRgbAString('#1D2225', 0.05)}`,
  grayOpacity07: `${hexToRgbAString('#1D2225', 0.07)}`,
  grayOpacity12: `${hexToRgbAString('#1D2225', 0.12)}`,
  grayOpacity22: `${hexToRgbAString('#1D2225', 0.22)}`,
  grayOpacity36: `${hexToRgbAString('#1D2225', 0.36)}`,
  grayOpacity45: `${hexToRgbAString('#1D2225', 0.45)}`,
  grayOpacity52: `${hexToRgbAString('#1D2225', 0.52)}`,
  grayOpacity67: `${hexToRgbAString('#1D2225', 0.67)}`,
  grayOpacity79: `${hexToRgbAString('#1D2225', 0.79)}`,
  grayOpacity92: `${hexToRgbAString('#1D2225', 0.92)}`,
};

export const BlueColors = {
  blue50: '#E6F2FF',
  blue100: '#C8E4FF',
  blue200: '#A7D4FF',
  blue300: '#83C2FF',
  blue400: '#5DAEFF',
  blue500: '#1B8DFB',
  blue600: '#0F73E5',
  blue700: '#005EC4',
  blue800: '#0750A7',
  blue900: '#074188',
  blueOpacity04: `${hexToRgbAString('#1B8DF8', 0.04)}`,
  blueOpacity07: `${hexToRgbAString('#1B8DF8', 0.07)}`,
  blueOpacity12: `${hexToRgbAString('#1B8DF8', 0.12)}`,
  blueOpacity22: `${hexToRgbAString('#1B8DF8', 0.22)}`,
  blueOpacity36: `${hexToRgbAString('#1B8DF8', 0.36)}`,
  blueOpacity45: `${hexToRgbAString('#1B8DF8', 0.45)}`,
  blueOpacity52: `${hexToRgbAString('#1B8DF8', 0.52)}`,
  blueOpacity67: `${hexToRgbAString('#1B8DF8', 0.67)}`,
  blueOpacity79: `${hexToRgbAString('#1B8DF8', 0.79)}`,
  blueOpacity92: `${hexToRgbAString('#1B8DF8', 0.92)}`,
};

export const RedColors = {
  red50: '#FFE4E6',
  red100: '#FFBEC3',
  red200: '#FF8F98',
  red300: '#FF6C79',
  red400: '#F84957',
  red500: '#EC2A3A',
  red600: '#DA2333',
  red700: '#BD0D1C',
  red800: '#A90815',
  red900: '#750710',
  redOpacity04: `${hexToRgbAString('#EE3C4B', 0.04)}`,
  redOpacity07: `${hexToRgbAString('#EE3C4B', 0.07)}`,
  redOpacity12: `${hexToRgbAString('#EE3C4B', 0.12)}`,
  redOpacity22: `${hexToRgbAString('#EE3C4B', 0.22)}`,
  redOpacity36: `${hexToRgbAString('#EE3C4B', 0.36)}`,
  redOpacity45: `${hexToRgbAString('#EE3C4B', 0.45)}`,
  redOpacity52: `${hexToRgbAString('#EE3C4B', 0.52)}`,
  redOpacity67: `${hexToRgbAString('#EE3C4B', 0.67)}`,
  redOpacity79: `${hexToRgbAString('#EE3C4B', 0.79)}`,
  redOpacity92: `${hexToRgbAString('#EE3C4B', 0.92)}`,
};

export const OrangeColors = {
  orange50: '#FFF1D5',
  orange100: '#FDE1AD',
  orange200: '#FFCA84',
  orange300: '#FFB75E',
  orange400: '#FFA136',
  orange500: '#FA8A00',
  orange600: '#E56E13',
  orange700: '#CF5F13',
  orange800: '#A34B03',
  orange900: '#793A1C',
};

export const GreenColors = {
  green50: '#E5F5ED',
  green100: '#B3E2CB',
  green200: '#70C99D',
  green300: '#49BA81',
  green400: '#30A86B',
  green500: '#2a945e',
  green600: '#248051',
  green700: '#1e6d45',
  green800: '#1a5c3b',
  green900: '#15452d',
};

export const TealColors = {
  teal50: '#E6F8F7',
  teal100: '#C7EEEC',
  teal200: '#A5E4E1',
  teal300: '#7DD8D3',
  teal400: '#4EC9C3',
  teal500: '#14B8B0',
  teal600: '#119B94',
  teal700: '#107E79',
  teal800: '#0C5A57',
  teal900: '#0A4A46',
  tealOpacity04: `${hexToRgbAString('#19B9B1', 0.04)}`,
  tealOpacity07: `${hexToRgbAString('#19B9B1', 0.07)}`,
  tealOpacity12: `${hexToRgbAString('#19B9B1', 0.12)}`,
  tealOpacity22: `${hexToRgbAString('#19B9B1', 0.22)}`,
  tealOpacity36: `${hexToRgbAString('#19B9B1', 0.36)}`,
  tealOpacity45: `${hexToRgbAString('#19B9B1', 0.45)}`,
  tealOpacity52: `${hexToRgbAString('#19B9B1', 0.52)}`,
  tealOpacity67: `${hexToRgbAString('#19B9B1', 0.67)}`,
  tealOpacity79: `${hexToRgbAString('#19B9B1', 0.79)}`,
  tealOpacity92: `${hexToRgbAString('#19B9B1', 0.92)}`,
};

export const PrimaryColors = {
  primary50: TealColors.teal50,
  primary100: TealColors.teal100,
  primary200: TealColors.teal200,
  primary300: TealColors.teal300,
  primary400: TealColors.teal400,
  primary500: TealColors.teal500,
  primary600: TealColors.teal600,
  primary700: TealColors.teal700,
  primary800: TealColors.teal800,
  primary900: TealColors.teal900,
  primaryOpacity04: TealColors.tealOpacity04,
  primaryOpacity07: TealColors.tealOpacity07,
  primaryOpacity12: TealColors.tealOpacity12,
  primaryOpacity22: TealColors.tealOpacity22,
  primaryOpacity36: TealColors.tealOpacity36,
  primaryOpacity45: TealColors.tealOpacity45,
  primaryOpacity52: TealColors.tealOpacity52,
  primaryOpacity67: TealColors.tealOpacity67,
  primaryOpacity79: TealColors.tealOpacity79,
  primaryOpacity92: TealColors.tealOpacity92,
};

export const Colors = {
  ...WhiteColors,
  ...BlackColors,
  ...GrayColors,
  ...BlueColors,
  ...RedColors,
  ...OrangeColors,
  ...GreenColors,
  ...TealColors,
  ...PrimaryColors,
  transparent: 'transparent',
};

export const SemanticColors = {
  primary: Colors.gray900,
  primaryHover: Colors.gray800,
  minus: Colors.red600,
  plus: Colors.green600,
  secondary50: Colors.blue50,
  secondary100: Colors.blue100,
  secondary200: Colors.blue200,
  secondary300: Colors.blue300,
  secondary400: Colors.blue400,
  secondary500: Colors.blue500,
  secondary600: Colors.blue600,
  secondary700: Colors.blue700,
  secondary800: Colors.blue800,
  secondary900: Colors.blue900,
  secondaryOpacity04: Colors.blueOpacity04,
  secondaryOpacity07: Colors.blueOpacity07,
  secondaryOpacity12: Colors.blueOpacity12,
  secondaryOpacity22: Colors.blueOpacity22,
  secondaryOpacity36: Colors.blueOpacity36,
  secondaryOpacity45: Colors.blueOpacity45,
  secondaryOpacity52: Colors.blueOpacity52,
  secondaryOpacity67: Colors.blueOpacity67,
  secondaryOpacity79: Colors.blueOpacity79,
  secondaryOpacity92: Colors.blueOpacity92,
} as const;
